"use client";

import type { TextAreaProps } from "@heroui/react";

import React from "react";
import { Textarea } from "@heroui/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";

interface PromptInputProps extends TextAreaProps {
  messagesCount?: number;
  isReadOnly?: boolean;
}

export const PromptInput = React.forwardRef<HTMLTextAreaElement, PromptInputProps>(
  ({ classNames = {}, messagesCount = 0, isReadOnly = false, ...props }, ref) => {
    const { t } = useTranslation();

    // Determine placeholder based on message count
    const placeholder = messagesCount === 0 ? t("Start new chat") : t("Type a message");

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        // Call form submission function
        props.onSubmit?.(event as unknown as React.FormEvent<HTMLInputElement>);
      }
    };

    return (
      <Textarea
        ref={ref}
        data-hj-suppress
        aria-label={t("Prompt")}
        className={cn("min-h-[40px] focus:outline-none", isReadOnly && "cursor-not-allowed")}
        classNames={{
          ...classNames,
          label: cn("hidden", classNames?.label),
          input: cn(
            "py-0 focus:outline-none [&::selection]:bg-brand-secondary [&::selection]:bg-brand-secondary [&::-moz-selection]:bg-brand-secondary [&::-moz-selection]:bg-brand-secondary",
            classNames?.input,
            isReadOnly && "opacity-70 cursor-not-allowed",
          ),
        }}
        data-id="prompt-input"
        disabled={props.disabled}
        minRows={1}
        placeholder={placeholder}
        radius="lg"
        readOnly={isReadOnly}
        variant="bordered"
        onKeyDown={handleKeyDown as any}
        {...props}
      />
    );
  },
);

PromptInput.displayName = "PromptInput";
